import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Collapse,
  Dialog,
  LinearProgress,
  CircularProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import {
  AccountCircle as AccountCircleIcon,
  Refresh,
  Visibility,
  VisibilityOff,
  ExpandLess,
  ExpandMore,
  ArrowBack,
  WorkspacePremium,
  AccountBalanceWalletOutlined,
  PaymentsOutlined,
  TimerOutlined,
  MailOutline,
  PersonOutline,
  PhoneIphone,
  FingerprintOutlined,
  AccountBalanceOutlined,
  BadgeOutlined,
  HistoryOutlined,
  PasswordOutlined,
  EventAvailableOutlined,
  CampaignOutlined,
  RedeemOutlined,
  CardGiftcardOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import ProfileWallet from "./ProfileWallet";
import Backdrop from "../components/common/Backdrop";
import SecurityCenter from "../components/wallet/SecurityCenter";
import IdVerification from "../components/wallet/IdVerification";
import {
  useGetProfileQuery,
  useGetShareLinkQuery,
  usePlayerBanksMutation,
  useVerifyIdentityMutation,
  useGetRankInfoQuery,
  useGetNextVipQuery,
  useManualUpgradeVipMutation,
} from "../features/api/userApiSlice";
import {
  useGetDepositDetailsQuery,
  useGetWithdrawDetailsQuery,
} from "../features/api/transactionApiSlice";
import { useGetVipRankDetailsQuery } from "../features/api/publicApiSlice";
import { useWithdrawPlayerBalanceFromLastPlatformMutation } from "../features/api/gameApiSlice";
import { apiSlice } from "../features/api/apiSlice";
import { findRankByNumber, findEligibleUpgrade, getVipRankLabel } from "../utils/vipRank";
import useNotificationCount from "../hooks/useNotificationCount";
import * as Constant from "../features/constant";
import useAccountVerification, {
  isVerifyAccountEnabled,
  isIdVerificationEnabled,
} from "../components/wallet/AccountVerification";

const HIDE_BALANCE_KEY = "profileWallet2.hideBalance";

const readHideBalance = () => {
  try {
    return localStorage.getItem(HIDE_BALANCE_KEY) === "true";
  } catch (e) {
    return false;
  }
};

// Screens that SecurityCenter / IdVerification navigate to via location.state.screen.
// Desktop renders them in the content pane; on mobile we show them as a full-screen overlay.
const OVERLAY_SCREENS = ["security center", "id verification"];

const NAMED_COLORS = { white: "#ffffff", black: "#000000" };
const safeAlpha = (color, value) => {
  try {
    return alpha(NAMED_COLORS[String(color).toLowerCase()] || color, value);
  } catch (e) {
    return color;
  }
};

// 950 → "950", 100000 → "100K", 1500000 → "1.5M", 2000000000 → "2B"
const formatCompact = (value) => {
  const num = Number(value || 0);
  const units = [
    { size: 1e9, suffix: "B" },
    { size: 1e6, suffix: "M" },
    { size: 1e3, suffix: "K" },
  ];
  const unit = units.find(({ size }) => Math.abs(num) >= size);
  if (!unit) return String(num);
  return `${parseFloat((num / unit.size).toFixed(1))}${unit.suffix}`;
};

const Card = ({ bg, children, sx }) => (
  <Box sx={{ backgroundColor: bg, borderRadius: 2, mx: 2, mt: 1.5, overflow: "hidden", ...sx }}>
    {children}
  </Box>
);

const ProfileWalletMobile = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();

  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";
  const missionEnabled = process.env.REACT_APP_MISSION_ENABLED === "true";
  const currency = process.env.REACT_APP_CURRENCY_CODE;
  const textColor = process.env.REACT_APP_COLOR_TEXT || (isLightTheme ? "#222" : "white");
  const mutedColor = safeAlpha(textColor, 0.55);
  const accent = theme.palette.primary.main;
  const cardBg = isLightTheme ? "#eef1f5" : theme.palette.background.paper;
  const tileBg = isLightTheme ? "#e6eaf0" : safeAlpha("#ffffff", 0.06);
  const stripBg = safeAlpha(accent, 0.08);

  const [hideBalance, setHideBalance] = useState(readHideBalance);
  const [appsExpanded, setAppsExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshCooldownUntilRef = useRef(0);

  const { totalUnread: messagingTotalUnread } = useNotificationCount();
  const { data: rankInfo } = useGetRankInfoQuery();
  const { data: nextVip } = useGetNextVipQuery();
  const { data: user, isLoading: isUserLoading } = useGetProfileQuery();
  const { data: shareLink, isLoading: isShareLinkLoading } = useGetShareLinkQuery();
  const { data: depositDetails, isLoading: isDepositDetailsLoading } = useGetDepositDetailsQuery();
  const { data: withdrawDetails, isLoading: isWithdrawDetailsLoading } = useGetWithdrawDetailsQuery();
  const [withdrawLastPlatform] = useWithdrawPlayerBalanceFromLastPlatformMutation();

  // Same rank / manual-upgrade resolution as ProfileWallet.
  const { data: vipRankDetails } = useGetVipRankDetailsQuery();
  const rankNumber = parseInt(String(user?.result?.playerRank || "").replace(/\D/g, ""), 10);
  const rankRecord = findRankByNumber(vipRankDetails?.result?.vipRanks, rankNumber);
  const customRankName = rankRecord?.customDisplayName?.trim();
  const eligibleUpgrade = findEligibleUpgrade(
    user?.result?.playerRank,
    user?.result?.accumulatedDeposit ?? user?.result?.totalDepositedAmount,
    vipRankDetails?.result?.vipRanks,
  );
  const [triggerManualUpgrade, { isLoading: isManualUpgradingVip }] = useManualUpgradeVipMutation();
  const handleManualUpgradeVip = async () => {
    try {
      const res = await triggerManualUpgrade().unwrap();
      if (res?.success) {
        const newRankLabel = getVipRankLabel({
          vipRank: parseInt(String(res?.result?.newRank || "").replace(/\D/g, ""), 10),
          customDisplayName: eligibleUpgrade?.customDisplayName,
        });
        Swal.fire({
          icon: "success",
          title: `Upgraded to ${newRankLabel}`,
          text: `${res?.result?.upgradeAmount || 0} ${currency || "USD"} bonus credited`,
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: "error", title: "Upgrade failed", text: res?.message || "Please try again" });
      }
    } catch (e) {
      Swal.fire({ icon: "error", title: "Upgrade failed", text: e?.data?.message || e?.message || "Please try again" });
    }
  };

  const verification = useAccountVerification({
    user: user?.result,
    mobile: true,
    shareLink: shareLink?.result?.url,
  });

  if (isUserLoading || isShareLinkLoading || isDepositDetailsLoading || isWithdrawDetailsLoading) {
    return <Backdrop />;
  }

  const profile = user?.result;
  const rankLabel = customRankName || profile?.playerRank;
  const rankLogo = rankInfo?.result?.privileges?.[profile?.playerRank]?.logo;
  const overlayScreen = String(location?.state?.screen || "").toLowerCase();
  const showOverlay = OVERLAY_SCREENS.includes(overlayScreen);

  const vipProgress = Math.min(
    ((nextVip?.result?.depositAmount || 0) / (nextVip?.result?.nextDepositAccumulate || 1)) * 100,
    100
  );
  const depositVipAmount = Math.max(
    0,
    Number(nextVip?.result?.nextDepositAccumulate) - Number(nextVip?.result?.depositAmount)
  );

  const closeOverlay = () =>
    navigate(Constant.getPath("/profileWallet"), { replace: true, state: {} });

  const toggleHideBalance = () => {
    const next = !hideBalance;
    setHideBalance(next);
    try {
      localStorage.setItem(HIDE_BALANCE_KEY, String(next));
    } catch (e) {
      // storage unavailable; keep in-memory state only
    }
  };

  const showRefreshCooldown = () => {
    if (Date.now() < refreshCooldownUntilRef.current) {
      Swal.fire({
        title: t("header.refreshingtitle"),
        text: t("header.refreshingbalance"),
        icon: "info",
      });
      return true;
    }
    return false;
  };

  // Same refresh as the DesktopHeader button: pull the balance back from the last
  // game platform, then reload the profile so the new balance shows.
  const handleRefresh = async () => {
    if (isRefreshing || showRefreshCooldown()) return;
    try {
      setIsRefreshing(true);
      await withdrawLastPlatform(profile?.userId).unwrap();
    } catch (err) {
      console.error("Withdrawal failed:", err);
    } finally {
      refreshCooldownUntilRef.current = Date.now() + 5000;
      setIsRefreshing(false);
      dispatch(apiSlice.util.invalidateTags(["Profile"]));
    }
  };

  const goDeposit = () =>
    navigate(Constant.getPath("/deposit"), { state: { depositDetails: depositDetails?.result } });
  const goWithdraw = () =>
    navigate(Constant.getPath("/withdrawal"), {
      state: { withdrawDetails: withdrawDetails?.result, user: profile },
    });
  const goHistory = () => navigate(Constant.getPath("/history"));
  const goMessaging = () => navigate(Constant.getPath("/messaging"));
  const goVip = () => navigate(Constant.getPath("/vip"));

  // ── Shared pieces ──
  const UnreadBadge = ({ count }) =>
    count > 0 ? (
      <Box
        component="span"
        sx={{
          position: "absolute",
          top: -6,
          right: -8,
          backgroundColor: "#d32f2f",
          color: "white",
          fontSize: "10px",
          fontWeight: 600,
          lineHeight: 1,
          height: 18,
          minWidth: 18,
          borderRadius: "9px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          px: "4px",
        }}
      >
        {count > 99 ? "99+" : count}
      </Box>
    ) : null;

  const ActionIcon = ({ label, Icon, onClick, badge, loading }) => (
    <Box
      onClick={onClick}
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={0.75}
      sx={{ cursor: "pointer", userSelect: "none" }}
    >
      <Typography fontSize="14px" color={textColor} lineHeight={1.2} textAlign="center">
        {label}
      </Typography>
      <Box position="relative" display="flex">
        {loading ? (
          <CircularProgress size={30} sx={{ color: accent }} />
        ) : (
          <Icon sx={{ fontSize: 34, color: accent }} />
        )}
        <UnreadBadge count={badge} />
      </Box>
    </Box>
  );

  // Same entries, order, labels and navigation state as ProfileWallet's screensMobile list.
  const tiles = [
    {
      label: t("walletcard.promocenter", "Promo Center"),
      Icon: CampaignOutlined,
      onClick: () =>
        navigate(Constant.getPath("/promocenter"), {
          state: { isNotProfileWallet: true, user: profile },
        }),
    },
    ...(missionEnabled
      ? [
        {
          label: t("mission.missiondiary", "Mission Diary"),
          Icon: EventAvailableOutlined,
          onClick: () =>
            navigate(Constant.getPath("/mission"), { state: { userId: profile?.userId } }),
        },
      ]
      : []),
    {
      label: t("walletcard.redemptioncode", "Redemption Code"),
      Icon: RedeemOutlined,
      onClick: () =>
        navigate(Constant.getPath("/redemptioncode"), {
          state: { isNotProfileWallet: true, user: profile },
        }),
    },
    {
      label: t("walletcard.ongoingbonus", "Ongoing Bonus"),
      Icon: CardGiftcardOutlined,
      onClick: () =>
        navigate(Constant.getPath("/ongoingbonus"), {
          state: { withdrawDetails: withdrawDetails?.result },
        }),
    },
    {
      label: t("walletcard.history", "History"),
      Icon: HistoryOutlined,
      onClick: goHistory,
    },
    {
      label: t("walletcard.myaccount", "My Account"),
      Icon: BadgeOutlined,
      onClick: () =>
        navigate(Constant.getPath("/myaccount"), {
          state: { user: profile, shareLink: shareLink?.result?.url },
        }),
    },
    {
      label: t("walletcard.bankAccount", "Bank Account"),
      Icon: AccountBalanceOutlined,
      onClick: () => navigate(Constant.getPath("/bankaccount")),
    },
    {
      label: t("walletcard.changepassword", "Change Password"),
      Icon: PasswordOutlined,
      onClick: () => navigate(Constant.getPath("/changepassword")),
    },
    {
      label: t("messaging.title", "Messaging"),
      Icon: MailOutline,
      badge: messagingTotalUnread,
      onClick: goMessaging,
    },
  ];

  const verificationIcon = {
    profile: PersonOutline,
    phone: PhoneIphone,
    email: MailOutline,
    id: FingerprintOutlined,
    bank: AccountBalanceOutlined,
  };

  const idStatus = profile?.isIdentityVerified
    ? "1/1"
    : profile?.isIdVerificationSubmitted
      ? t("walletcard.pending", "Pending")
      : "0/1";

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.secondaryPaper,
        minHeight: "100vh",
        // Clears the fixed mobile app bar (original mobile view used ~64px) plus a little breathing room.
        pt: { xs: 9, sm: 3 },
        pb: 12,
      }}
    >
      {/* ── Header: avatar, username, rank pills ── */}
      <Box display="flex" alignItems="center" gap={1} px={2}>
        <AccountCircleIcon sx={{ fontSize: 34, color: theme.palette.warning.main, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize="17px" color={accent} noWrap>
            {profile?.username}
          </Typography>
          {missionEnabled && (
            <Typography fontSize="12px" color={mutedColor} noWrap>
              {profile?.activityPoint || 0} Points
            </Typography>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="center"
          gap={0.5}
          onClick={goVip}
          sx={{ cursor: "pointer", backgroundColor: theme.palette.secondary.main, borderRadius: "20px", pl: 0.5, pr: 1.25, py: 0.25 }}
        >
          {rankLogo && <Box component="img" src={rankLogo} sx={{ width: 18, height: 18 }} />}
          <Typography fontSize="12px" fontWeight="bold" color="white" noWrap>
            {rankLabel}
          </Typography>
        </Box>
        {rankRecord?.accumulatedDeposit != null && (
          <Box
            onClick={goVip}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer", backgroundColor: accent, borderRadius: "20px", pl: 0.25, pr: 1.25, py: 0.25 }}
          >
            <WorkspacePremium sx={{ fontSize: 20, color: "#f5c518" }} />
            {/* Deposit requirement the player has met for their current rank */}
            <Typography fontSize="12px" fontWeight="bold" color="white" noWrap>
              {`>${formatCompact(rankRecord.accumulatedDeposit)}`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Wallet card ── */}
      <Card bg={cardBg}>
        <Box px={2} pt={2} pb={1.5}>
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography fontSize="14px" color={textColor}>
                    {t("walletcard.walletbalance", "Wallet Balance")}
                  </Typography>
                  <IconButton size="small" onClick={toggleHideBalance} sx={{ p: 0.25 }}>
                    {hideBalance ? (
                      <VisibilityOff sx={{ fontSize: 18, color: "#e5534b" }} />
                    ) : (
                      <Visibility sx={{ fontSize: 18, color: "#e5534b" }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: isLightTheme ? "white" : safeAlpha("#ffffff", 0.08),
                  borderRadius: "20px",
                  px: 2,
                  py: 0.75,
                }}
              >
                <Typography fontSize="11px" color={mutedColor}>
                  {currency}
                </Typography>
                <Typography fontSize="17px" color={accent} sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {hideBalance ? "****" : Number(profile?.currentCredit || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1.5} pl={0.5}>
              <ActionIcon
                label={t("walletcard.refresh", "Refresh")}
                Icon={Refresh}
                onClick={handleRefresh}
                loading={isRefreshing}
              />
              <ActionIcon label={t("walletcard.deposit", "Deposit")} Icon={AccountBalanceWalletOutlined} onClick={goDeposit} />
            </Box>
          </Box>

          <Collapse in={appsExpanded} timeout="auto">
            <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" mt={2.5} rowGap={2}>
              <ActionIcon label={t("walletcard.withdrawal", "Withdraw")} Icon={PaymentsOutlined} onClick={goWithdraw} />
              <ActionIcon label={t("walletcard.history", "History")} Icon={TimerOutlined} onClick={goHistory} />
              <ActionIcon
                label={t("walletcard.message", "Message")}
                Icon={MailOutline}
                onClick={goMessaging}
                badge={messagingTotalUnread}
              />
            </Box>
          </Collapse>
        </Box>

        <Box
          onClick={() => setAppsExpanded((prev) => !prev)}
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
          py={0.75}
          sx={{ cursor: "pointer", backgroundColor: stripBg, color: accent }}
        >
          {appsExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          <Typography fontSize="13px" color={accent}>
            {appsExpanded
              ? t("walletcard.lessapplications", "Less applications")
              : t("walletcard.moreapplications", "More applications")}
          </Typography>
        </Box>
      </Card>

      {/* ── User verification ── */}
      {isVerifyAccountEnabled() && (
        <Card bg={cardBg} sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography fontSize="15px" fontWeight="bold" color={textColor}>
            {t("walletcard.userverification", "User Verification")}
          </Typography>
          <Box display="flex" gap={1.5}>
            {verification.items.map(({ key, verified, onClick }) => {
              const Icon = verificationIcon[key];
              return (
              <Box
                key={key}
                onClick={onClick}
                sx={{
                  cursor: "pointer",
                  width: 30,
                  height: 30,
                  borderRadius: 1,
                  backgroundColor: isLightTheme ? "white" : safeAlpha("#ffffff", 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon sx={{ fontSize: 20, color: verified ? accent : mutedColor }} />
              </Box>
              );
            })}
          </Box>
        </Card>
      )}

      {/* ── VIP rank: same gold card as the original ProfileWallet mobile view ── */}
      <Box
        display="flex"
        flexDirection="column"
        onClick={goVip}
        sx={{
          cursor: "pointer",
          px: 2,
          mx: 2,
          mt: 1.5,
          py: 1.5,
          borderRadius: 2,
          background: `
            repeating-linear-gradient(
              45deg,
              rgba(255, 255, 255, 0.08),
              rgba(255, 255, 255, 0.08) 2px,
              transparent 2px,
              transparent 10px
            ),
            linear-gradient(135deg, #b8860b 0%, #ffcc33 100%)
          `,
        }}
      >
        <Typography fontWeight={"bold"} fontSize={"x-large"} color={"#3E2723"}>
          {t("walletcard.viprank")}
        </Typography>

        <Typography fontSize={"x-small"} color={"#3E2723"}>
          {(() => {
            const depositVipText = t("walletcard.depositvip", { amount: depositVipAmount });
            const depositVipAmountStr = String(depositVipAmount);
            const idx = depositVipText.indexOf(depositVipAmountStr);
            if (idx === -1) return depositVipText;
            return (
              <>
                {depositVipText.slice(0, idx)}
                <Box component="span" sx={{ color: process.env.REACT_APP_COLOR_PRIMARY || theme.palette.primary.main, fontWeight: "bold" }}>
                  {` ${depositVipAmount.toLocaleString()} ${currency}`}
                </Box>
                {depositVipText.slice(idx + depositVipAmountStr.length)}
              </>
            );
          })()}
        </Typography>

        {eligibleUpgrade && (
          <Box mt={1}>
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                // Card itself navigates to /vip; keep the upgrade click from bubbling up.
                e.stopPropagation();
                handleManualUpgradeVip();
              }}
              disabled={isManualUpgradingVip}
              sx={{
                backgroundColor: "#4ade80",
                color: "#000",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textTransform: "none",
                px: 1.5,
                py: 0.25,
                minWidth: "auto",
                borderRadius: "6px",
                "&:hover": { backgroundColor: "#22c55e" },
              }}
            >
              {t("vipnew.upgradenow", {
                rank: getVipRankLabel(eligibleUpgrade),
                defaultValue: `Upgrade to ${getVipRankLabel(eligibleUpgrade)}`,
              })}
            </Button>
          </Box>
        )}

        <Box mt={1} display={"flex"} flexDirection={"row"} justifyContent={"space-between"}>
          <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
            {rankLabel}
          </Typography>
          <Typography fontWeight={"bold"} fontSize={"small"} color={"#3E2723"}>
            {nextVip?.result?.nextPlayerRank}
          </Typography>
        </Box>

        <LinearProgress
          variant="buffer"
          value={vipProgress}
          valueBuffer={100}
          color="primary"
          sx={{
            my: 1,
            width: "100%",
            borderRadius: 5,
            height: 5,
            "& .MuiLinearProgress-bar2Buffer": {
              backgroundColor: "#888",
            },
          }}
        />

        <Box display={"flex"} flexDirection={"row"} justifyContent={"center"}>
          <Typography fontWeight={"bold"} fontSize={"x-small"} color={"#3E2723"}>
            {`${Math.round(vipProgress)}%`}
          </Typography>
        </Box>
      </Box>

      {/* ── ID verification (KYC slot) ── */}
      {isIdVerificationEnabled() && (
      <Card bg={cardBg} sx={{ px: 2, py: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography fontSize="15px" fontWeight="bold" color={textColor} sx={{ flex: 1 }}>
          {t("walletcard.idverification", "ID Verification")}
        </Typography>
        <CheckCircleOutline sx={{ fontSize: 20, color: profile?.isIdentityVerified ? accent : mutedColor }} />
        <Typography fontSize="14px" color={mutedColor} mr={1.5}>
          {idStatus}
        </Typography>
        <Button
          variant="contained"
          disabled={!!profile?.isIdentityVerified}
          onClick={verification.actions.id.onClick}
          sx={{
            textTransform: "none",
            borderRadius: 1.5,
            px: 3,
            boxShadow: "none",
            backgroundColor: "#8c98a4",
            "&:hover": { backgroundColor: "#76828e", boxShadow: "none" },
          }}
        >
          {profile?.isIdentityVerified
            ? t("walletcard.verified", "Verified")
            : t("walletcard.verify", "Verify")}
        </Button>
      </Card>
      )}

      {/* ── Feature tiles ── */}
      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={1} mx={2} mt={2.5}>
        {tiles.map(({ label, Icon, onClick, badge }) => (
          <Box
            key={label}
            onClick={onClick}
            sx={{
              cursor: "pointer",
              backgroundColor: tileBg,
              borderRadius: 1,
              aspectRatio: "1 / 1",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.25,
              px: 1,
              "&:active": { opacity: 0.8 },
            }}
          >
            <Box position="relative" display="flex">
              <Icon sx={{ fontSize: 42, color: accent }} />
              <UnreadBadge count={badge} />
            </Box>
            <Typography fontSize="14px" color={textColor} textAlign="center" lineHeight={1.2}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {verification.dialogs}

      {/* ── Security center / ID verification overlay ── */}
      <Dialog
        fullScreen
        open={showOverlay}
        onClose={closeOverlay}
        PaperProps={{ sx: { backgroundColor: theme.palette.background.secondaryPaper } }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          px={1}
          py={1}
          sx={{ backgroundColor: cardBg, position: "sticky", top: 0, zIndex: 1 }}
        >
          <IconButton onClick={closeOverlay}>
            <ArrowBack sx={{ color: accent }} />
          </IconButton>
          <Typography fontSize="17px" color={textColor}>
            {overlayScreen === "id verification"
              ? t("walletcard.idverification", "ID Verification")
              : t("walletcard.userverification", "User Verification")}
          </Typography>
        </Box>
        <Box px={2} py={2}>
          {overlayScreen === "security center" && (
            <SecurityCenter usePlayerBanksMutation={usePlayerBanksMutation} />
          )}
          {overlayScreen === "id verification" && (
            <IdVerification useVerifyIdentityMutation={useVerifyIdentityMutation} />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

const ProfileWallet2 = () => {
  const theme = useTheme();
  // Same breakpoint ProfileWallet uses to switch between its desktop and mobile layouts.
  const mobileView = useMediaQuery(theme.breakpoints.down("lg"));
  return mobileView ? <ProfileWalletMobile /> : <ProfileWallet />;
};

export default ProfileWallet2;
